import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, ArrowUpRight } from "lucide-react";

const scrollTo = (hash) => (e) => {
  e.preventDefault();
  if (window.__lenis) window.__lenis.scrollTo(hash, { offset: -72 });
  else document.querySelector(hash)?.scrollIntoView({ behavior: "smooth" });
};

const line = {
  hidden: { y: "112%" },
  show: (i) => ({
    y: "0%",
    transition: { duration: 1, delay: 0.15 + i * 0.13, ease: [0.16, 1, 0.3, 1] },
  }),
};

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "14%"]);

  const lines = ["Book a private", "consultation with", "Will French."];

  return (
    <section id="top" ref={ref} className="relative overflow-hidden bg-white pt-[72px]">
      <div className="mx-auto grid max-w-7xl grid-cols-1 items-center gap-14 px-5 pb-20 pt-12 sm:px-8 lg:grid-cols-[1.1fr_0.9fr] lg:gap-20 lg:pb-28 lg:pt-20">
        <div>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6 }}
            className="mb-7 flex items-center gap-4"
          >
            <motion.span
              initial={{ scaleX: 0 }}
              animate={{ scaleX: 1 }}
              transition={{ duration: 0.9, delay: 0.1, ease: [0.16, 1, 0.3, 1] }}
              className="h-px w-14 origin-left bg-gold"
            />
            <p className="text-[11px] font-semibold uppercase tracking-[0.3em] text-smoke">
              REALTOR® · Team Partner · CHK Real Estate
            </p>
          </motion.div>

          <h1 className="font-serif text-[2.9rem] font-medium leading-[1.04] tracking-tight text-ink sm:text-6xl lg:text-[4.4rem]">
            {lines.map((l, i) => (
              <span key={l} className="block overflow-hidden pb-1">
                <motion.span
                  className="block"
                  variants={line}
                  initial="hidden"
                  animate="show"
                  custom={i}
                >
                  {l}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.65 }}
            className="mt-7 max-w-md text-[15px] font-light leading-relaxed text-smoke sm:text-base"
          >
            Thirty minutes, direct with Will — a confidential, no-pressure conversation about your
            property, its value, and the plan for what comes next.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.8 }}
            className="mt-9 flex flex-wrap items-center gap-4"
          >
            <a
              href="#book"
              onClick={scrollTo("#book")}
              data-testid="hero-cta-book"
              className="group flex items-center gap-3 bg-ink px-7 py-4 text-[12px] font-semibold uppercase tracking-[0.2em] text-white transition-transform duration-200 hover:scale-[1.02]"
            >
              Book your consultation
              <ArrowDown size={14} className="transition-transform duration-200 group-hover:translate-y-0.5" />
            </a>
            <a
              href="tel:+16475359455"
              data-testid="hero-cta-call"
              className="border border-ink px-7 py-4 text-[12px] font-semibold uppercase tracking-[0.2em] text-ink transition-colors hover:bg-ink hover:text-white"
            >
              Call (647) 535-9455
            </a>
            <a
              href="https://www.corriehardingteam.com"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="hero-cta-website"
              className="flex items-center gap-2 border border-hairline px-7 py-4 text-[12px] font-semibold uppercase tracking-[0.2em] text-smoke transition-colors hover:border-ink hover:text-ink"
            >
              Visit the full CHK Real Estate website
              <ArrowUpRight size={14} />
            </a>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 1.05 }}
            className="mt-12 flex flex-wrap items-center gap-x-8 gap-y-3 border-t border-hairline pt-6"
          >
            {["$75M+ Career Sales", "$850M+ Team Sales", "Top 1% TRREB Team", "Readers' Choice 2025"].map((s) => (
              <span key={s} className="text-[11px] font-semibold uppercase tracking-[0.2em] text-smoke">
                {s}
              </span>
            ))}
          </motion.div>
        </div>

        <div className="relative">
          <motion.div
            initial={{ opacity: 0, scale: 0.97 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1.1, delay: 0.35, ease: [0.16, 1, 0.3, 1] }}
            className="relative aspect-[4/5] overflow-hidden bg-greige"
          >
            <motion.img
              src="/hero-property.webp"
              alt="Modern black-clad luxury home with a waterfront-inspired backyard — listed by Will French, CHK Real Estate"
              data-testid="hero-image"
              className="h-[115%] w-full object-cover"
              style={{ y: imgY }}
            />
            <div className="absolute inset-x-0 bottom-0 h-2/5 bg-gradient-to-t from-ink/85 via-ink/35 to-transparent" />
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.9, delay: 1.15 }}
              className="absolute inset-x-0 bottom-7 z-10 flex justify-center"
            >
              <img
                src="/chk-logo-white.png"
                alt="Will French of CHK Real Estate — Royal LePage"
                data-testid="hero-logo-overlay"
                className="w-[78%] max-w-[340px]"
              />
            </motion.div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.9 }}
            className="absolute -left-4 -top-5 z-20 border border-hairline bg-white p-1.5 shadow-[0_12px_40px_rgba(0,0,0,0.08)] sm:-left-8"
          >
            <img
              src="https://media-production.lp-cdn.com/cdn-cgi/image/format=auto,quality=85,fit=scale-down,width=1280/https://media-production.lp-cdn.com/media/8af3ebb4-8cb1-4d2f-86dc-13b5e3fdb76e"
              alt="Will French, REALTOR® — CHK Real Estate"
              data-testid="hero-headshot"
              className="object-cover"
              style={{ width: "8.5rem", height: "11rem" }}
            />
            <p className="px-1 pt-2 pb-1 text-center text-[9px] font-semibold uppercase tracking-[0.24em] text-smoke">
              Will French
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.9, delay: 1.05 }}
            className="absolute -top-5 right-4 bg-ink px-5 py-4 text-ivory sm:right-[-1.5rem]"
          >
            <p className="font-serif text-2xl leading-none">$75M+</p>
            <p className="mt-1 text-[9px] uppercase tracking-[0.24em] text-ivory/70">Career Sales</p>
          </motion.div>
        </div>
      </div>
    </section>
  );
}